import {
  Autocomplete,
  InputAdornment,
  Paper,
  PopperProps,
  TextField,
  ThemeProvider,
} from "@mui/material";
import {
  createContext,
  CSSProperties,
  forwardRef,
  ReactNode,
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CLIENT_CANDIDATES_LABEL,
  CLIENT_LEGACY_PROMPT,
  CLIENT_NO_RESULTS,
  CLIENT_PICKER_PROMPT,
  CLIENT_POLICY_SEARCH_FAILED,
  CLIENT_POLICY_SEARCH_UNAVAILABLE,
  CLIENT_POLICY_SEARCHING,
  CLIENT_RETRY,
  CLIENT_ROW_SEPARATOR,
  CLIENT_SEARCH_FAILED,
} from "~/common/workflows/clientPicker/clientPickerCopy";
import { clientRowDetail } from "~/common/workflows/clientPicker/clientPickerRows";
import type { ClientSearchSource } from "~/common/workflows/clientPicker/clientSearchSource";
import { useClientSearch } from "~/common/workflows/clientPicker/useClientSearch";
import { toClientOption } from "~/common/workflows/clientSearchOption";
import type { ClientSearchResult } from "~/common/workflows/types/client";
import { UserSessionDetails } from "~/features/auth/auth.types";
import { ClientsDto } from "~/features/clients/clients.types";
import { ClientOption } from "~/features/expertises/community-corner/components/ClientTypeAheadDropdown";
import {
  getAutocompleteOptionClasses,
  getAutocompleteTheme,
} from "../utilities/autoCompleteTheme";
import { shouldShowBranchForPersona } from "../utilities/clientContextUtils";
import SearchIcon from "./svgs/SearchIcon";

export function mapClientDtoToOption({
  clientSk,
  clientName,
  clientEmail,
  clientPhoneNumber,
  displayStrategyName,
  clientCity,
  clientState,
  clientPostalCode,
  branchName,
  locationName,
  clientType,
  policyCount,
  matchedPolicyNumber,
}: ClientsDto & {
  /** Story 28998: null or absent = unknown. */
  policyCount?: number | null;
  matchedPolicyNumber?: string | null;
}): ClientOption {
  return {
    id: clientSk ?? 0,
    name: clientName || "",
    clientEmail: clientEmail?.toLowerCase() === "unknown" ? "" : clientEmail,
    clientPhoneNumber:
      clientPhoneNumber?.toLowerCase() === "unknown" ? "" : clientPhoneNumber,
    displayStrategyName: displayStrategyName || "",
    clientSk,
    clientCity: clientCity || "",
    clientState: clientState || "",
    clientPostalCode: clientPostalCode || "",
    branchName: branchName || "",
    locationName: locationName || "",
    clientType: clientType || "",
    policyCount: typeof policyCount === "number" ? policyCount : undefined,
    matchedPolicyNumber: matchedPolicyNumber || undefined,
  };
}

export interface ClientOptionDetailSegment {
  text: string;
  isLocation: boolean;
}

/**
 * Builds the ordered segments of the search-result detail line:
 * `[cityStatePostal, policySegment?, branch?, location?, type?]`. The policy
 * segment is the matched policy number (policy-number hits) or "N policies";
 * an unknown count adds nothing, never "0 policies" (story 28998). The Location
 * segment is flagged `isLocation` (retained for the data model + its tests);
 * as of US23748 UAT the renderer prints every segment as plain text, so the
 * flag no longer drives any styling. Branch is included only for
 * branch-visible personas. US 24656.
 */
export function buildClientOptionSegments(
  option: ClientOption,
  showBranch: boolean
): ClientOptionDetailSegment[] {
  const [address] = clientRowDetail({
    city: option.clientCity,
    state: option.clientState,
    postalCode: option.clientPostalCode,
  });
  const [policy] = clientRowDetail({
    policyCount: option.policyCount,
    matchedPolicyNumber: option.matchedPolicyNumber,
  });

  const segments: ClientOptionDetailSegment[] = [];
  if (address) segments.push({ text: address, isLocation: false });
  if (policy) segments.push({ text: policy, isLocation: false });
  if (showBranch && option.branchName?.trim())
    segments.push({ text: option.branchName.trim(), isLocation: false });
  if (option.locationName?.trim())
    segments.push({ text: option.locationName.trim(), isLocation: true });
  if (option.clientType?.trim())
    segments.push({ text: option.clientType.trim(), isLocation: false });
  return segments;
}

/* ---- popup pieces -------------------------------------------------------- */

interface PickerSurface {
  inline: boolean;
  /** Failure / searching / empty rows, drawn under the rows (or alone). */
  status: ReactNode;
}
const PickerSurfaceContext = createContext<PickerSurface>({ inline: false, status: null });

/**
 * The popup surface. Floating: MUI's Paper. Inline: a plain block in the
 * card's flow (the box's border belongs to the control's wrapper). Both carry
 * the status rows after the options.
 */
const PickerPaper = forwardRef<
  HTMLDivElement,
  { children?: ReactNode; className?: string; style?: CSSProperties }
>(function PickerPaper({ children, className, style }, ref) {
  const { inline, status } = useContext(PickerSurfaceContext);
  const body = (
    <>
      {children}
      {status}
    </>
  );
  return inline ? (
    <div ref={ref} className={className} style={style}>
      {body}
    </div>
  ) : (
    <Paper ref={ref} className={className} style={style}>
      {body}
    </Paper>
  );
});

/** In-flow stand-in for MUI's Popper: no portal, no positioning. */
function InlinePopper({ children, open, role }: PopperProps) {
  return open ? (
    <div role={role}>{typeof children === "function" ? null : children}</div>
  ) : null;
}

function CheckMark() {
  return (
    <svg
      data-testid="client-row-check"
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M3.5 8.5l3 3 6-7"
        stroke="#079455"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const STATUS_ROW = "px-3 py-[10px] text-[12.5px]";
const SEARCHING_TEXT = "Searching…";
const MIN_CHARS = 3;
const NO_ROWS: ClientSearchResult[] = [];

/* ---- the control --------------------------------------------------------- */

type SourceProps = {
  /**
   * Where rows come from: a name leg and an optional policy-number leg.
   * Need not be memoised (the control calls the latest one), but a
   * different source mid-query does not restart the search.
   */
  source: ClientSearchSource;
};

export type ClientQuicklinkDropdownProps = SourceProps & {
  id: string;
  /** The prompt. Default: the picker prompt when the source has a policy leg, else the legacy one. */
  label?: string;
  /** A picked row, as the workflow contract (`ClientSearchResult`). A cleared input is not a pick. */
  onPick?: (row: ClientSearchResult) => void;
  /** The header's contract: the picked `ClientOption`, or "" when the field is cleared. */
  onSelect?: (option: ClientOption | string) => void;
  onBlurAction?: () => void;
  helperText?: string;
  error?: boolean;
  isMobile?: boolean;
  user?: UserSessionDetails | null;
  /** Focus the input on mount — the header opens this on a click, so it
   *  defaults on; a search box that is simply part of a page passes false. */
  autoFocus?: boolean;
  /**
   * In a page's flow rather than the header: no fixed header height (the
   * header's `h-[15px]` let the field spill over whatever sat below it) and
   * the pane's one control height, 34px, with 13px text (story 28633).
   */
  compact?: boolean;
  /**
   * "Possible matches from this email", listed while the input is empty. Pass
   * them already merged with the current client (`candidateRows`).
   */
  candidates?: ClientSearchResult[];
  /** The row that carries the check (the current client). */
  selectedSk?: number | null;
  /** The highlighted row (arrows or pointer); null when it goes away or the list closes. */
  onHighlight?: (row: ClientSearchResult | null) => void;
  /**
   * List in the card's flow (pane, launch form): the list is always open while
   * mounted, blur never picks, Escape is left to the host's frame (it
   * bubbles), Enter is always the control's (stopped here; with no highlight it
   * picks nothing), Tab moves focus. Default: the floating
   * popup (header).
   */
  inline?: boolean;
};

const ClientQuicklinkDropdown = ({
  id,
  label,
  onPick,
  onSelect,
  onBlurAction,
  helperText,
  error,
  isMobile = false,
  user,
  source,
  autoFocus = true,
  compact = false,
  candidates,
  selectedSk = null,
  onHighlight,
  inline = false,
}: ClientQuicklinkDropdownProps) => {
  const [value, setValue] = useState<ClientSearchResult | null>(null);
  const [inputValue, setInputValue] = useState("");
  /** What is searched: the input, except MUI's "reset" after a pick (the name it writes back). */
  const [query, setQuery] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const clientSearchRef = useRef<HTMLInputElement>(null);

  // `useClientSearch` restarts on a new source identity, and hosts hand in
  // fresh objects every render: give it one stable proxy to the latest.
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const hasPolicyLeg = typeof source.searchByPolicy === "function";
  const stableSource = useMemo<ClientSearchSource>(
    () => ({
      searchByName: (q) => sourceRef.current.searchByName(q),
      ...(hasPolicyLeg
        ? {
            searchByPolicy: (q: string) =>
              sourceRef.current.searchByPolicy?.(q) ??
              Promise.resolve({ status: "unavailable" as const }),
          }
        : {}),
    }),
    [hasPolicyLeg]
  );

  const search = useClientSearch(stableSource, query);

  const trimmedQuery = query.trim();
  const typed = trimmedQuery.length >= MIN_CHARS;
  const candidateList = candidates ?? NO_ROWS;
  const showCandidates = trimmedQuery === "" && candidateList.length > 0;
  const options = typed ? search.rows : showCandidates ? candidateList : NO_ROWS;

  const status = useMemo<ReactNode>(() => {
    if (!typed) return null;
    const rows: ReactNode[] = [];
    if (search.nameFailed) {
      rows.push(
        <FailureRow key="failed" text={CLIENT_SEARCH_FAILED} onRetry={search.retry} />
      );
    } else {
      if (search.policyLeg === "failed") {
        rows.push(
          <FailureRow key="policy-failed" text={CLIENT_POLICY_SEARCH_FAILED} onRetry={search.retry} />
        );
      }
      if (search.rows.length === 0) {
        if (search.settled && search.policyLeg !== "failed") {
          rows.push(
            <div key="none" role="status" className={`${STATUS_ROW} text-center text-[#667085]`}>
              {CLIENT_NO_RESULTS}
            </div>
          );
        } else if (!search.settled) {
          rows.push(
            <div key="searching" role="status" className={`${STATUS_ROW} text-center text-[#667085]`}>
              {SEARCHING_TEXT}
            </div>
          );
        }
      } else if (search.policyLeg === "loading") {
        rows.push(
          <div key="policy-searching" role="status" className={`${STATUS_ROW} text-[#667085]`}>
            {CLIENT_POLICY_SEARCHING}
          </div>
        );
      }
      if (search.policyLeg === "unavailable") {
        rows.push(
          <div key="policy-unavailable" role="status" className={`${STATUS_ROW} text-[#667085]`}>
            {CLIENT_POLICY_SEARCH_UNAVAILABLE}
          </div>
        );
      }
    }
    return rows.length > 0 ? (
      <div
        role="presentation"
        // A press here must not blur the input (the popup would close under the click).
        onMouseDown={(event) => event.preventDefault()}
        className={options.length > 0 ? "border-t border-[#EAECF0]" : undefined}
      >
        {rows}
      </div>
    ) : null;
  }, [typed, search, options.length]);

  const hasContent = options.length > 0 || status !== null;
  const open = inline ? hasContent : popupOpen && hasContent;

  // Tell the host when the highlight is gone because the list went away.
  const onHighlightRef = useRef(onHighlight);
  onHighlightRef.current = onHighlight;
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) onHighlightRef.current?.(null);
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (autoFocus) clientSearchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const pick = (row: ClientSearchResult) => {
    onPick?.(row);
    onSelect?.(toClientOption(row));
  };

  const handleBlur = () => {
    // Floating (header) only: typing a client's exact name and leaving picks
    // it, as before. Inline never applies a client the person did not pick.
    if (!inline) {
      const selectedOption = options.find((option) => option.clientName === inputValue);
      if (selectedOption) {
        setValue(selectedOption);
        pick(selectedOption);
      }
    }
    onBlurAction?.();
  };

  const handleValueChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: ClientSearchResult | null
  ) => {
    if (newValue) {
      if (!inline) setValue(newValue);
      pick(newValue);
    } else if (event.type !== "keydown") {
      // Cleared from the clear button (not a keystroke): the header's "".
      setValue(null);
      onSelect?.("");
    }
  };

  const handleInputChange = (
    _event: SyntheticEvent<Element, Event>,
    newInputValue: string,
    reason: string
  ) => {
    setInputValue(newInputValue);
    if (reason !== "reset" || newInputValue === "") setQuery(newInputValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      // Never submit a surrounding form; MUI still picks a highlighted row.
      event.preventDefault();
    } else if (event.key === "Tab") {
      if (!inline) event.preventDefault();
    } else if (event.key === "Escape" && inline) {
      // The host's frame owns Escape (cancel); MUI would swallow it.
      (event as unknown as { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
    }
  };

  const theme = useMemo(() => getAutocompleteTheme(isMobile), [isMobile]);
  const showBranch = shouldShowBranchForPersona(user?.userAccessLevel);
  const rowClasses = inline
    ? "px-3 py-[9px] border-b border-[#F2F4F7] last:border-b-0 cursor-pointer"
    : getAutocompleteOptionClasses(isMobile, false);

  const renderClientOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key?: string },
    row: ClientSearchResult
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- MUI's option class is replaced
    const { key, className: _muiClassName, ...liProps } = optionProps;
    const detail = buildClientOptionSegments(toClientOption(row), showBranch)
      .map((segment) => segment.text)
      .join(CLIENT_ROW_SEPARATOR);
    const selected = selectedSk !== null && row.clientSk === selectedSk;
    return (
      <li key={key} {...liProps} role="option" aria-selected={selected} className={rowClasses}>
        <div className="flex w-full items-center gap-[10px]">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs xl1440:text-sm font-semibold text-gray-900">
              {row.clientName}
            </span>
            {detail ? (
              <span className="mt-px block truncate text-2xs xl1440:text-xs text-gray-600">
                {detail}
              </span>
            ) : null}
          </span>
          {selected ? <CheckMark /> : null}
        </div>
      </li>
    );
  };

  const surface = useMemo<PickerSurface>(() => ({ inline, status }), [inline, status]);

  return (
    <div
      role="presentation"
      className={`${id}-container`}
      // Enter never reaches a host's own key handling: it is the control's (pick the highlighted row).
      onKeyDown={(event) => {
        if (event.key === "Enter") event.stopPropagation();
      }}
    >
      <ThemeProvider theme={theme}>
        <PickerSurfaceContext.Provider value={surface}>
          <div className={inline ? "overflow-hidden rounded-lg border border-[#EAECF0]" : undefined}>
            <Autocomplete<ClientSearchResult, false, false, false>
              id={id}
              value={inline ? null : value}
              size="small"
              onChange={handleValueChange}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onBlur={handleBlur}
              options={options}
              open={open}
              onOpen={inline ? undefined : () => setPopupOpen(true)}
              onClose={inline ? undefined : () => setPopupOpen(false)}
              openOnFocus={!inline && candidateList.length > 0}
              onHighlightChange={(_event, row) => onHighlight?.(row ?? null)}
              getOptionLabel={(row) => row.clientName}
              getOptionKey={(row) => row.clientSk}
              isOptionEqualToValue={(a, b) => a.clientSk === b.clientSk}
              filterOptions={(x) => x} // Disable client-side filtering
              groupBy={showCandidates ? () => CLIENT_CANDIDATES_LABEL : undefined}
              renderGroup={(params) => (
                <li key={params.key}>
                  <div className="border-b border-[#EAECF0] bg-[#F9FAFB] px-3 pb-2 pt-1 text-[11px] text-[#98A2B3]">
                    {params.group}
                  </div>
                  <ul className="p-0">{params.children}</ul>
                </li>
              )}
              renderOption={(props, row) => renderClientOption(props, row)}
              clearOnEscape={!inline}
              clearOnBlur={!inline}
              selectOnFocus={false}
              disablePortal={inline}
              // Loading and "no options" are the status rows (PickerPaper), never MUI's text.
              loading={false}
              noOptionsText=""
              PopperComponent={inline ? InlinePopper : undefined}
              PaperComponent={PickerPaper}
              ListboxProps={
                inline ? { style: { padding: 0, marginTop: 0, maxHeight: 260 } } : undefined
              }
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-noOptions": { display: "none" },
                    "& .MuiAutocomplete-option.Mui-focused": {
                      backgroundColor: `${inline ? "#F2F4F7" : "#FFF8C9"} !important`,
                    },
                    ...(inline
                      ? {
                          border: "none",
                          boxShadow: "none",
                          borderRadius: 0,
                          overflow: "visible",
                          "& .MuiAutocomplete-option[aria-selected='true']": {
                            backgroundColor: "#F9FAFB !important",
                          },
                          "& .MuiAutocomplete-option:hover": {
                            backgroundColor: "#F9FAFB !important",
                          },
                        }
                      : {}),
                  },
                },
              }}
              sx={
                inline
                  ? {
                      background: "#F9FAFB",
                      padding: "8px",
                      borderBottom: hasContent ? "1px solid #EAECF0" : "none",
                    }
                  : undefined
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={clientSearchRef}
                  placeholder={label ?? (hasPolicyLeg ? CLIENT_PICKER_PROMPT : CLIENT_LEGACY_PROMPT)}
                  variant="outlined"
                  className={
                    compact
                      ? "bg-white"
                      : "bg-white h-[15px] xl1440:h-[40px] text-2xs xl1440:text-sm"
                  }
                  sx={
                    compact
                      ? {
                          "& .MuiInputBase-root": {
                            height: "34px",
                            paddingTop: 0,
                            paddingBottom: 0,
                            fontSize: "13px",
                          },
                        }
                      : undefined
                  }
                  error={error}
                  helperText={helperText}
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="w-[16px] h-[16px] xl1440:w-[25px] xl1440:h-[24px]" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              className="w-full"
            />
          </div>
        </PickerSurfaceContext.Provider>
      </ThemeProvider>
    </div>
  );
};

function FailureRow({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className={`${STATUS_ROW} flex items-center justify-between gap-2 text-[#B42318]`}
    >
      <span>{text}</span>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 font-semibold underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise"
      >
        {CLIENT_RETRY}
      </button>
    </div>
  );
}

export default ClientQuicklinkDropdown;
